import { isPresenceStatus, type NotificationEvent, type PresenceStatus } from "@xop/dashboard-shared";
import type { Update } from "@tauri-apps/plugin-updater";

import { parseDeepLinkRoute } from "../../components/share/appShareLinks.js";

/**
 * Bridge to the Tauri desktop shell. In a plain browser these are no-ops
 * (the web UI shows an in-app toast instead); inside the desktop webview
 * they raise a real native OS notification and bring the window forward.
 *
 * The `@tauri-apps/*` modules are imported lazily so the browser bundle
 * never evaluates them unless we're actually running inside Tauri.
 */
export function isTauri(): boolean {
  return (
    typeof window !== "undefined" &&
    ("__TAURI_INTERNALS__" in window || "__TAURI__" in window)
  );
}

/**
 * Ask the OS for notification permission. Call this on launch so the prompt
 * appears in the foreground instead of lazily on the first event. Returns
 * whether permission is granted. No-op (false) in the browser.
 *
 * NOTE (macOS): the OS only registers/authorizes notifications for a
 * properly code-signed app (Developer ID). An **ad-hoc-signed** build never
 * registers, so this returns false and no prompt appears — that's a macOS
 * requirement, not a bug. Windows (installed, via the NSIS AppUserModelID)
 * and Linux (D-Bus) have no such restriction.
 */
export async function requestNotificationPermission(): Promise<boolean> {
  if (!isTauri()) return false;
  try {
    const mod = await import("@tauri-apps/plugin-notification");
    let granted = await mod.isPermissionGranted();
    if (!granted) granted = (await mod.requestPermission()) === "granted";
    if (!granted) console.warn("[desktop] OS notification permission not granted");
    return granted;
  } catch (e) {
    console.warn("[desktop] notification permission request failed:", e);
    return false;
  }
}

/**
 * Raise a native OS notification. Returns whether it was actually shown.
 * Errors are logged (not swallowed) so a permission/signing failure is
 * visible in the webview console instead of looking like "nothing happened".
 */
export async function notifyNative(
  ev: Pick<NotificationEvent, "title" | "body" | "sound">,
): Promise<boolean> {
  if (!isTauri()) return false;
  try {
    const mod = await import("@tauri-apps/plugin-notification");
    let granted = await mod.isPermissionGranted();
    if (!granted) granted = (await mod.requestPermission()) === "granted";
    if (!granted) {
      console.warn("[desktop] native notification suppressed — permission not granted");
      return false;
    }
    // `sound: "default"` plays the OS notification sound (the banner is
    // otherwise silent). Only high-signal events (a chat @mention) request it.
    mod.sendNotification({
      title: ev.title,
      body: ev.body,
      ...(ev.sound ? { sound: "default" } : {}),
    });
    return true;
  } catch (e) {
    console.warn("[desktop] native notification failed:", e);
    return false;
  }
}

/**
 * Fire a native notification locally (no server round-trip) to directly test
 * the OS-popup path. Returns whether it was shown.
 *
 * NOTE: the dashboard's live notification stream NO LONGER uses native OS
 * notifications — it uses the custom bottom-right popup ({@link
 * showDesktopNotification}) so a notification never opens / steals focus to
 * the whole app, and it can appear bottom-right (macOS pins native
 * notifications top-right). `notifyNative` is retained as a building block /
 * possible future fallback.
 */
export async function testNativeNotification(): Promise<boolean> {
  return notifyNative({
    title: "Test notification",
    body: "If you see this popup, desktop notifications are working.",
  });
}

/**
 * Show a notification as a CUSTOM bottom-right popup card — the desktop
 * equivalent of the in-app snackbar, rendered in a dedicated frameless,
 * transparent, always-on-top, click-through window pinned to the bottom-right
 * of the screen. Crucially it appears WITHOUT opening / focusing the main app
 * (it shows even while the app is hidden in the tray), so a new lead / ticket
 * pops a card on screen without the whole window spawning open.
 *
 * Delegates to the Rust shell's `show_notification` command, which positions +
 * shows the popup window and injects the card. No-op (false) in the browser —
 * the web build shows the in-app snackbar instead.
 */
export async function showDesktopNotification(
  ev: Pick<NotificationEvent, "id" | "title" | "body" | "severity">,
): Promise<boolean> {
  if (!isTauri()) return false;
  try {
    const { invoke } = await import("@tauri-apps/api/core");
    await invoke("show_notification", {
      payload: {
        id: ev.id,
        title: ev.title,
        body: ev.body,
        severity: ev.severity,
      },
    });
    return true;
  } catch (e) {
    console.warn("[desktop] notification popup failed:", e);
    return false;
  }
}

/**
 * Deliver a live notification on the desktop, **OS-native first**. The whole
 * point (per the operator): use the platform's built-in notification centre
 * (macOS Notification Center / Windows Action Center / Linux D-Bus) — these
 * are shown by the OS itself, so they appear even while the app is **minimised
 * or in the background**, which the custom in-app popup window could not do
 * reliably (it depends on the app's own window state). Used for ALL
 * categories (chat, leads, CRM, marketing, tickets, …) via the dispatcher.
 *
 * Falls back to the custom bottom-right popup ONLY when the native banner
 * can't be shown — chiefly an **ad-hoc-signed macOS build**, where the OS
 * refuses to register notifications (see `notifyNative`). So a properly signed
 * install gets true OS notifications; a dev/unsigned build still shows
 * something rather than nothing. No-op in the browser (the snackbar handles it).
 *
 * NOTE on deep background: this still runs in the webview, so it requires the
 * web layer's WebSocket to deliver the event. A minimised window keeps that
 * connection alive; a webview the OS has fully suspended (rare) would miss the
 * live banner and pick the event up from the bell on resume. A Rust-side
 * socket listener is the documented follow-up for guaranteed delivery while
 * fully suspended — see docs/notifications.md.
 */
export async function deliverDesktopNotification(
  ev: Pick<NotificationEvent, "id" | "title" | "body" | "severity" | "sound">,
): Promise<void> {
  if (!isTauri()) return;
  const shownNatively = await notifyNative(ev);
  if (!shownNatively) await showDesktopNotification(ev);
}

/**
 * Fire the custom bottom-right popup locally (no server round-trip) — used by
 * the bell's "Test" button so a user can confirm the on-screen popup works.
 */
export async function testDesktopNotification(): Promise<boolean> {
  return showDesktopNotification({
    id: `local-test:${Date.now()}`,
    title: "Test notification",
    body: "If you see this bottom-right popup, desktop notifications are working.",
    severity: "info",
  });
}

/**
 * Bring the main window forward: show it (it may be hidden in the tray
 * after a close-to-tray), unminimize, and focus. When `attention` is set
 * it ALSO flashes the taskbar / bounces the macOS dock via
 * `requestUserAttention` — the reliable "look at me" signal that still
 * fires when the OS refuses to let a background app steal focus outright.
 * No-op outside Tauri.
 */
async function bringMainWindowForward(attention: boolean): Promise<void> {
  if (!isTauri()) return;
  try {
    const { getCurrentWindow, UserAttentionType } = await import(
      "@tauri-apps/api/window"
    );
    const w = getCurrentWindow();
    await w.show();
    await w.unminimize();
    await w.setFocus();
    if (attention) {
      try {
        await w.requestUserAttention(UserAttentionType.Critical);
      } catch {
        /* capability missing / older shell — the focus above already ran */
      }
    }
  } catch {
    /* not in Tauri / API shape changed — ignore */
  }
}

/** Show + focus the main window (no attention flash). Used when the user
 * is already in the app — e.g. clicking a bell item to deep-link. */
export async function focusDesktopWindow(): Promise<void> {
  await bringMainWindowForward(false);
}

/**
 * Surface the app for an INCOMING notification: show + focus the main
 * window AND flash for attention. This is the "automatically fire up the
 * application when a new notification arrives" behaviour. Because it is
 * pure window management (not the OS notification centre), it works
 * regardless of code signing — so the app still surfaces even when the
 * native banner is silently suppressed on an ad-hoc-signed macOS build.
 * No-op in the browser.
 */
export async function presentDesktopWindow(): Promise<void> {
  await bringMainWindowForward(true);
}

/**
 * Handle an INCOMING 1:1 call on the desktop app: raise + focus the window (so
 * the in-app ringing modal is actually visible, even from the tray / behind
 * other apps) AND fire the native "X is calling" banner with sound. A ringing
 * call is a hard, time-critical interrupt, so this always surfaces (it does not
 * consult the notification-mute prefs). No-op in the browser, where the
 * IncomingCallModal + WebAudio ringtone already cover it.
 */
export async function surfaceIncomingCall(callerName: string): Promise<void> {
  if (!isTauri()) return;
  await presentDesktopWindow();
  await notifyNative({
    title: "Incoming call",
    body: `${callerName} is calling`,
    sound: true,
  });
}

/**
 * Subscribe to status commands from the desktop **tray** ("Status ▸ Available
 * / Away / Do Not Disturb"). The Rust shell emits `xop://set-status` with the
 * chosen status; we forward it to the caller (the RealtimeProvider) so the
 * tray and the in-app profile dropdown drive the ONE server-side status — and
 * DND set from the tray silences notifications server-side. Returns an
 * unlisten function; a no-op resolver in the browser.
 */
export async function onDesktopStatusCommand(
  cb: (status: PresenceStatus) => void,
): Promise<() => void> {
  if (!isTauri()) return () => undefined;
  try {
    const { listen } = await import("@tauri-apps/api/event");
    return await listen<string>("xop://set-status", (e) => {
      if (isPresenceStatus(e.payload)) cb(e.payload);
    });
  } catch (e) {
    console.warn("[desktop] status-command listener failed:", e);
    return () => undefined;
  }
}

/**
 * Ask the desktop shell how long the WHOLE COMPUTER has been idle, in seconds
 * (0 = input right now). Resolves `null` in a plain browser — which has no such
 * API and never will — and on a platform the shell can't read (Linux today), so
 * callers must treat `null` as "no signal", not "not idle".
 */
export async function getSystemIdleSeconds(): Promise<number | null> {
  if (!isTauri()) return null;
  try {
    const { invoke } = await import("@tauri-apps/api/core");
    const secs = await invoke<number | null>("system_idle_seconds");
    return typeof secs === "number" && Number.isFinite(secs) ? secs : null;
  } catch {
    /* older shell without the command — in-app activity still drives presence */
    return null;
  }
}

/**
 * Subscribe to the shell's OS-wide idle samples. A Rust thread reads the
 * platform idle clock every few seconds and emits `xop://system-idle`; this
 * forwards each reading so presence tracks the person, not the window.
 *
 * The sampling deliberately lives in Rust: the app spends most of its life
 * hidden in the tray, where the webview's own timers are throttled to roughly
 * once a minute — precisely the situation (user at their desk, working in
 * another app) this is here to report. Returns an unlisten fn; a no-op resolver
 * in the browser.
 */
export async function onSystemIdle(cb: (seconds: number) => void): Promise<() => void> {
  if (!isTauri()) return () => undefined;
  try {
    const { listen } = await import("@tauri-apps/api/event");
    return await listen<number>("xop://system-idle", (e) => {
      if (typeof e.payload === "number" && Number.isFinite(e.payload)) cb(e.payload);
    });
  } catch (e) {
    console.warn("[desktop] system-idle listener failed:", e);
    return () => undefined;
  }
}

/**
 * Subscribe to **desktop deep-link opens** (`xopstaff://...`). When the OS hands
 * the installed app a clicked share link, the Rust shell brings the window
 * forward and emits `xop://open-route` with the raw URL; we parse it to an
 * in-app route path and hand it to `cb` (which navigates). This is how a shared
 * "Copy link" / emailed link opens the DESKTOP APP at the right record instead
 * of the browser. Returns an unlisten fn; a no-op resolver in the browser.
 */
export async function onDesktopOpenRoute(cb: (path: string) => void): Promise<() => void> {
  if (!isTauri()) return () => undefined;
  try {
    const { listen } = await import("@tauri-apps/api/event");
    return await listen<string>("xop://open-route", (e) => {
      const path = parseDeepLinkRoute(e.payload);
      if (path) cb(path);
    });
  } catch (e) {
    console.warn("[desktop] open-route listener failed:", e);
    return () => undefined;
  }
}

/**
 * Subscribe to the main window regaining **focus** — i.e. the user "opened"
 * the app. On a single-instance, close-to-tray shell that is NOT a remount:
 * the process has been alive for days, so anything that should happen when
 * the app is opened has to hang off this, not off a React mount effect.
 * (Consumer: the update re-check — see `useDesktopUpdate`.) Returns an
 * unlisten fn; a no-op resolver in the browser.
 */
export async function onDesktopWindowFocus(cb: () => void): Promise<() => void> {
  if (!isTauri()) return () => undefined;
  try {
    const { getCurrentWindow } = await import("@tauri-apps/api/window");
    return await getCurrentWindow().onFocusChanged(({ payload: focused }) => {
      if (focused) cb();
    });
  } catch (e) {
    console.warn("[desktop] focus listener failed:", e);
    return () => undefined;
  }
}

/**
 * Drain a deep link captured at **cold start** - the OS launched the app BY
 * opening an `xopstaff://...` link, before this webview mounted and could listen.
 * The Rust shell stashes it; we drain it once on mount and return the in-app
 * route path (or null). Subsequent (warm) opens come through
 * {@link onDesktopOpenRoute} instead.
 */
export async function consumePendingDeepLinkRoute(): Promise<string | null> {
  if (!isTauri()) return null;
  try {
    const { invoke } = await import("@tauri-apps/api/core");
    const url = await invoke<string | null>("consume_pending_route");
    return url ? parseDeepLinkRoute(url) : null;
  } catch (e) {
    console.warn("[desktop] consume_pending_route failed:", e);
    return null;
  }
}

/**
 * Mirror the live unread-chat total onto the desktop **tray icon** — the Rust
 * shell bakes the number into the icon as a red count bubble (capped "99+"),
 * and clears it back to the plain icon at 0. So the count is visible even while
 * the app sits hidden in the tray. Delegates to the shell's `set_tray_badge`
 * command (see lib.rs + badge.rs). No-op in a plain browser (the in-app nav
 * badge already covers that). Errors are logged, never thrown — a stale tray
 * badge must never break the chat UI.
 */
export async function setTrayUnreadBadge(count: number): Promise<void> {
  if (!isTauri()) return;
  try {
    const { invoke } = await import("@tauri-apps/api/core");
    await invoke("set_tray_badge", { count: Math.max(0, Math.floor(count)) });
  } catch (e) {
    console.warn("[desktop] tray badge update failed:", e);
  }
}

/** Signal the Rust shell that the web app has mounted and is ready, so it
 * can close the splash window and reveal the main window. No-op in the
 * browser. */
export async function signalAppReady(): Promise<void> {
  if (!isTauri()) return;
  try {
    const { invoke } = await import("@tauri-apps/api/core");
    await invoke("app_ready");
  } catch {
    /* command not registered (older shell) — ignore */
  }
}

/**
 * Trigger the desktop self-update check — the SAME path as the tray
 * "Check for Updates…" item. Both emit the `menu://check-update` event that
 * {@link useDesktopUpdate} listens for, so this runs the exact same manual
 * `checkNow()` flow (shows "Checking…" → the `<UpdateBanner/>` modal with
 * "Update available" / "Update now" / "Restart now", or the "you're on the
 * latest version" toast). Used by the in-chat "Update now" button on the
 * "XOP Updates" release announcement.
 *
 * No-op (false) in a plain browser — there is no desktop updater there, and
 * the announcement stays text-only. Returns whether the check was dispatched.
 */
export async function triggerDesktopUpdateCheck(): Promise<boolean> {
  if (!isTauri()) return false;
  try {
    const { emit } = await import("@tauri-apps/api/event");
    await emit("menu://check-update");
    return true;
  } catch (e) {
    console.warn("[desktop] update-check trigger failed:", e);
    return false;
  }
}

/**
 * Look for a newer signed desktop release. Returns the pending Update
 * handle (pass it to {@link installDesktopUpdate}) or `null` when the app
 * is up to date / not running in the desktop shell / offline.
 *
 * The minisign signature is verified by the plugin against the pubkey
 * baked into tauri.conf.json BEFORE any bytes are applied — an unsigned or
 * tampered artifact is rejected. Best-effort: offline / no published
 * update / older shell all resolve to `null` rather than throwing.
 *
 * NOTE: unlike the old `checkForUpdates`, this does NOT auto-install. The
 * UI surfaces a visible "update available" prompt and the user decides —
 * see useDesktopUpdate + <UpdateBanner/>.
 *
 * Contract (deliberate — the old version swallowed ALL errors, so a failed
 * check looked identical to "no update", and the manual "Check for Updates…"
 * then falsely reported "you're on the latest version"):
 *   - returns `null` for the genuine "nothing to do" cases: not running in
 *     Tauri, OR the updater plugin isn't available (older shell / browser);
 *   - returns `null` when the feed says we're up to date;
 *   - **throws** when the check itself FAILS (no endpoint reachable, network
 *     drop, signature/manifest error) — so callers can tell "up to date"
 *     from "couldn't check" and surface the difference. The error is also
 *     logged so a "why isn't the dialog opening" report is diagnosable in
 *     the webview console.
 */
export async function getDesktopUpdate(): Promise<Update | null> {
  if (!isTauri()) return null;
  let check: (typeof import("@tauri-apps/plugin-updater"))["check"];
  try {
    ({ check } = await import("@tauri-apps/plugin-updater"));
  } catch {
    /* updater plugin unavailable (older shell / plain browser) — nothing to do */
    return null;
  }
  try {
    const update = await check();
    return update?.available ? update : null;
  } catch (e) {
    console.warn("[desktop] update check failed:", e);
    throw e instanceof Error ? e : new Error(String(e));
  }
}

/** Progress of an in-flight update download. `percent` is null while the
 * total size is still unknown (before the first "Started" event). */
export interface DownloadProgress {
  downloaded: number;
  contentLength: number | null;
  percent: number | null;
}

/**
 * Download + install a pending update, reporting progress as it streams.
 * The bytes are written + the installer applied on disk; the new version
 * takes effect after {@link relaunchApp}. Throws on failure (signature
 * mismatch, network drop) so the caller can show a retry affordance.
 */
export async function installDesktopUpdate(
  update: Update,
  onProgress?: (p: DownloadProgress) => void,
): Promise<void> {
  let downloaded = 0;
  let contentLength: number | null = null;
  await update.downloadAndInstall((event) => {
    switch (event.event) {
      case "Started":
        contentLength = event.data.contentLength ?? null;
        onProgress?.({ downloaded: 0, contentLength, percent: contentLength ? 0 : null });
        break;
      case "Progress":
        downloaded += event.data.chunkLength;
        onProgress?.({
          downloaded,
          contentLength,
          percent: contentLength
            ? Math.min(100, Math.round((downloaded / contentLength) * 100))
            : null,
        });
        break;
      case "Finished":
        onProgress?.({ downloaded, contentLength, percent: 100 });
        break;
    }
  });
}

/**
 * Relaunch the desktop app — used right after an update installs so the user
 * lands on the new version in one click. macOS / Windows / Linux.
 *
 * Routes through the shell's own `restart_app` command rather than
 * tauri-plugin-process `relaunch()`, because `relaunch()` re-execs with the
 * CURRENT args: a login-autostart instance starts with `--hidden`, so an
 * in-place relaunch after an update comes back HIDDEN — running in the tray
 * with NO window, which looks exactly like "the app didn't come back up after
 * it updated" (the user only sees it again on a manual re-open). `restart_app`
 * starts a fresh, VISIBLE instance (no `--hidden`) AFTER this process exits and
 * frees the single-instance lock. Falls back to the plugin relaunch on an
 * older shell that predates the command. No-op in the browser.
 */
export async function relaunchApp(): Promise<void> {
  if (!isTauri()) return;
  try {
    const { invoke } = await import("@tauri-apps/api/core");
    await invoke("restart_app");
    return;
  } catch {
    /* older shell without restart_app — fall through to the plugin relaunch */
  }
  try {
    const { relaunch } = await import("@tauri-apps/plugin-process");
    await relaunch();
  } catch {
    /* process plugin unavailable (older shell) — update applies next launch */
  }
}
